import React, { useEffect, useState } from 'react';
import { Button, Col, Row, Space, Tag, Typography } from 'antd';
import { FundProjectionScreenOutlined, RiseOutlined, TeamOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../store/LanguageContext';

const { Title, Paragraph, Text } = Typography;

interface FundingTool {
  id: number;
  name: string;
  slug: string;
  fundingAmount: number;
  shortDescription: string;
  logo?: string;
}

function formatFunding(amount: number, zh: boolean): string {
  if (!amount) return zh ? '未披露' : 'Undisclosed';
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}B`;
  return `$${amount}M`;
}

const FundingPage: React.FC = () => {
  const { language } = useLanguage();
  const zh = language === 'zh-CN';
  const navigate = useNavigate();
  const [tools, setTools] = useState<FundingTool[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/funding/spotlight', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setTools(Array.isArray(data.tools) ? data.tools : []);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="insights-page">
      <section className="insights-hero">
        <div className="insights-hero-inner">
          <div>
            <Tag color="geekblue">{zh ? '融资与投资' : 'Funding'}</Tag>
            <Title level={1}>{zh ? 'AI 工具融资速览' : 'AI Funding Spotlight'}</Title>
            <Paragraph>
              {zh
                ? '展示目录内已知融资靠前的 AI 产品。需要融资曝光或寻找标的，可留下意向。'
                : 'Top funded AI tools from our catalog. Seeking capital or deal flow? Leave your intent.'}
            </Paragraph>
            <Space wrap>
              <Button
                type="primary"
                icon={<RiseOutlined />}
                onClick={() => navigate('/submit?intent=seeking_funding')}
              >
                {zh ? '我要融资曝光' : 'Seek funding exposure'}
              </Button>
              <Button
                icon={<TeamOutlined />}
                onClick={() => navigate('/feedback?type=BUSINESS&intent=investing')}
              >
                {zh ? '我是投资人' : 'I am an investor'}
              </Button>
              <Button type="link" onClick={() => navigate('/feedback?type=BUSINESS&intent=seeking_funding')}>
                {zh ? '商务合作反馈' : 'Business inquiry'}
              </Button>
            </Space>
          </div>
          <div className="insights-stat-panel">
            <div>
              <strong>{tools.length || '—'}</strong>
              <span>{zh ? '融资样本' : 'samples'}</span>
            </div>
            <FundProjectionScreenOutlined style={{ fontSize: 28 }} />
          </div>
        </div>
      </section>

      <main className="insights-container">
        <Row gutter={[18, 18]}>
          {tools.map((tool) => (
            <Col xs={24} md={12} lg={8} key={tool.id}>
              <article className="insight-card">
                <div className="insight-card-meta">
                  <Tag color="gold">{formatFunding(tool.fundingAmount, zh)}</Tag>
                </div>
                <Title level={4} style={{ marginTop: 8 }}>
                  <Link to={`/tools/${tool.slug}`}>{tool.name}</Link>
                </Title>
                <Paragraph ellipsis={{ rows: 3 }}>
                  {tool.shortDescription || (zh ? '暂无简介' : 'No description')}
                </Paragraph>
                <Text type="secondary">
                  <Link to={`/tools/${tool.slug}`}>{zh ? '查看工具' : 'View tool'}</Link>
                </Text>
              </article>
            </Col>
          ))}
        </Row>
        {!tools.length ? (
          <div style={{ marginTop: 32, padding: '40px 24px', background: '#f8fafc', borderRadius: 16, textAlign: 'center' }}>
            <Paragraph style={{ fontSize: 16, color: '#334155', marginBottom: 8 }}>
              {zh ? '融资样本还在整理中' : 'Funding samples are being prepared'}
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 20 }}>
              {zh
                ? '需要融资曝光或寻找标的，可先留下意向；有公开样本后会显示在这里。'
                : 'Leave an intent if you need exposure or deal flow. Public samples will appear here.'}
            </Paragraph>
            <Button type="primary" onClick={() => navigate('/submit?intent=seeking_funding')}>
              {zh ? '留下融资意向' : 'Leave a funding intent'}
            </Button>
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default FundingPage;
